import { useState, useRef, useEffect, useCallback, type FormEvent, type KeyboardEvent, type ClipboardEvent } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Mail, ArrowLeft, RotateCw } from "lucide-react";
import { apiPost } from "../lib/api";
import { useAuth } from "../lib/auth";
import type { WorkspaceSession } from "../lib/workspaces";
import { getAttribution } from "../lib/attribution";

function pushEvent(event: string, params?: Record<string, unknown>) {
  // dataLayer push for GTM
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...params });
  // gtag() direct call as fallback
  if (typeof window.gtag === 'function') {
    window.gtag('event', event, params);
  }
}

// Plans that map to a self-serve Stripe checkout. The marketing site links here
// as `/register?plan=professional`; we stash the choice so it survives the
// magic-link / OTP email round-trip and prompts a checkout on first Billing view.
const CHECKOUT_PLANS = ["starter", "professional", "business"];
const INTENDED_PLAN_KEY = "vms_intended_plan";

export function Register() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const prefillEmail = (location.state as { email?: string } | null)?.email ?? "";

  useEffect(() => {
    const plan = searchParams.get("plan")?.toLowerCase();
    if (plan && CHECKOUT_PLANS.includes(plan)) {
      localStorage.setItem(INTENDED_PLAN_KEY, plan);
    }
  }, [searchParams]);
  const [form, setForm] = useState({
    tenantName: "",
    uen: "",
    email: prefillEmail,
    name: "",
    contactPhone: "",
    heardAboutUs: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [verifying, setVerifying] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const formStartFired = useRef(false);
  const pageViewFired = useRef(false);

  useEffect(() => {
    if (!pageViewFired.current) {
      pageViewFired.current = true;
      pushEvent("register_page_view");
      pushEvent("register_form_view");
    }
  }, []);

  const handleFormStart = useCallback(() => {
    if (!formStartFired.current) {
      formStartFired.current = true;
      pushEvent("register_form_start");
    }
  }, []);

  const focusedFields = useRef<Set<string>>(new Set());
  const handleFieldFocus = useCallback((fieldName: string) => {
    if (!focusedFields.current.has(fieldName)) {
      focusedFields.current.add(fieldName);
      pushEvent("register_field_focus", { field_name: fieldName });
    }
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  async function handleResend() {
    if (resendCooldown > 0 || resending) return;
    setResending(true);
    setError(null);
    try {
      await apiPost("/auth/login", {
        email: form.email,
        isRegistration: true,
        registrationData: {
          tenantName: form.tenantName,
          uen: form.uen.replace(/\s+/g, "").toUpperCase(),
          name: form.name || undefined,
          contactPhone: form.contactPhone.trim(),
          attribution: getAttribution(),
          heardAboutUs: form.heardAboutUs,
        },
      });
      setResendCooldown(60);
    } catch {
      setError("Could not resend code. Please try again.");
    } finally {
      setResending(false);
    }
  }

  function update(field: string, value: string) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    pushEvent("register_form_submit_attempt");
    pushEvent("register_form_submit");
    try {
      const res = await apiPost("/auth/login", {
        email: form.email,
        isRegistration: true,
        registrationData: {
          tenantName: form.tenantName,
          uen: form.uen.replace(/\s+/g, "").toUpperCase(),
          name: form.name || undefined,
          contactPhone: form.contactPhone.trim(),
          attribution: getAttribution(),
          heardAboutUs: form.heardAboutUs,
        },
      });
      if (!res.success) {
        setError(res.error?.message || "Something went wrong. Please try again.");
        pushEvent("register_form_error", { reason: res.error?.key || "server_error" });
        return;
      }
      pushEvent("sign_up", { method: "email" });
      pushEvent("register_otp_sent");
      setLinkSent(true);
    } catch {
      setError("Network error — please check your connection and try again.");
      pushEvent("register_form_error", { reason: "network_error" });
    } finally {
      setLoading(false);
    }
  }

  async function verifyCode(digits: string[]) {
    const code = digits.join("");
    if (code.length !== 6) return;
    setVerifying(true);
    setError(null);
    pushEvent("register_otp_submit");
    try {
      const res = await apiPost<WorkspaceSession & { isNewUser?: boolean; needsRegistration?: boolean }>("/auth/otp/verify", { email: form.email, code });
      if (res.success && res.data?.token && res.data.tenant) {
        pushEvent("register_otp_verified");
        login(res.data);
        const hasIntendedPlan = Boolean(localStorage.getItem(INTENDED_PLAN_KEY));
        const prefix = `/w/${res.data.tenant.slug}`;
        const dest = hasIntendedPlan ? `${prefix}/billing` : res.data.isNewUser ? "/welcome" : `${prefix}/checkins`;
        if (res.data.isNewUser) {
          window.dataLayer?.push({
            event: "registration_complete",
            user_id: res.data.user?.id,
            tenant_id: res.data.tenant?.id,
            eventCallback: () => navigate(dest, { replace: true }),
            eventTimeout: 2000,
          });
        } else {
          navigate(dest, { replace: true });
        }
      } else {
        setError(res.error?.message || "Invalid code");
        pushEvent("register_otp_error", { reason: res.error?.key || "invalid_code" });
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
        setVerifying(false);
      }
    } catch {
      setError("Network error");
      pushEvent("register_otp_error", { reason: "network_error" });
      setVerifying(false);
    }
  }

  function handleOtpChange(index: number, value: string) {
    if (!/^\d*$/.test(value)) return;
    const next = [...otp];
    next[index] = value.slice(-1);
    setOtp(next);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    if (value && index === 5) {
      verifyCode(next);
    }
  }

  function handleOtpKeyDown(index: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  }

  function handleOtpPaste(e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    const next = [...otp];
    for (let i = 0; i < 6; i++) next[i] = pasted[i] || "";
    setOtp(next);
    if (pasted.length === 6) {
      verifyCode(next);
    } else {
      inputRefs.current[Math.min(pasted.length, 5)]?.focus();
    }
  }

  const otpViewFired = useRef(false);
  useEffect(() => {
    if (linkSent && !otpViewFired.current) {
      otpViewFired.current = true;
      pushEvent("register_otp_view");
    }
  }, [linkSent]);

  if (linkSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4">
        <div className="w-full max-w-sm">
          <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-8 text-center animate-[fadeIn_0.3s_ease-out]">
            <div className="mx-auto w-14 h-14 rounded-full bg-brand-50 flex items-center justify-center mb-5">
              <Mail className="w-6 h-6 text-brand-500" />
            </div>
            <h1 className="text-2xl font-semibold text-stone-900 mb-2 font-display">Check your email</h1>
            <p className="text-sm text-stone-500 leading-relaxed mb-6">
              We sent a verification code to{" "}
              <span className="font-medium text-stone-700">{form.email}</span>.
              Enter the 6-digit code below.
            </p>

            <div className="flex justify-center gap-2 mb-4">
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => { inputRefs.current[i] = el; }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  onPaste={i === 0 ? handleOtpPaste : undefined}
                  disabled={verifying}
                  className="w-10 h-12 text-center text-lg font-semibold border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent disabled:opacity-50"
                  autoFocus={i === 0}
                />
              ))}
            </div>

            {verifying && <p className="text-sm text-stone-500 mb-3">Verifying...</p>}
            {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg mb-3">{error}</p>}

            <p className="text-xs text-stone-400 mb-2">Or click the link in the email</p>

            <button
              type="button"
              onClick={handleResend}
              disabled={resendCooldown > 0 || resending}
              className="inline-flex items-center gap-1.5 text-sm text-brand-500 hover:text-brand-700 font-medium transition-colors disabled:text-stone-400 disabled:cursor-not-allowed mb-4"
            >
              <RotateCw className={`w-3.5 h-3.5 ${resending ? "animate-spin" : ""}`} />
              {resending
                ? "Sending..."
                : resendCooldown > 0
                  ? `Resend code (${resendCooldown}s)`
                  : "Resend code"}
            </button>

            <div className="pt-4 border-t border-stone-100">
              <button
                onClick={() => { setLinkSent(false); setOtp(["", "", "", "", "", ""]); setError(null); }}
                className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-700 font-medium transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Use a different email
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4">
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-6">
          <div className="flex items-center gap-2">
            <img src="/singvms-seal.svg" alt="SingVMS" className="w-9 h-9" />
            <span className="text-xl font-bold text-stone-900 font-display">SingVMS</span>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-8">
          <h1 className="text-2xl font-semibold text-stone-900 mb-1 font-display">Create account</h1>
          <p className="text-sm text-stone-500 mb-6">Set up your business on SingVMS</p>

          <form onSubmit={handleSubmit} onFocusCapture={handleFormStart} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Business Name</label>
              <input
                type="text"
                value={form.tenantName}
                onChange={(e) => update("tenantName", e.target.value)}
                onFocus={() => handleFieldFocus("company_name")}
                required
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                placeholder="ABC Pte Ltd"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                UEN <span className="text-stone-400 font-normal">(business registration no.)</span>
              </label>
              <input
                type="text"
                value={form.uen}
                onChange={(e) => update("uen", e.target.value.toUpperCase())}
                onFocus={() => handleFieldFocus("uen")}
                required
                autoCapitalize="characters"
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm uppercase focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                placeholder="201912345A"
              />
              <p className="text-xs text-stone-400 mt-1">
                We use your UEN to verify your business before activating your account.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Your Name <span className="text-stone-400 font-normal">(optional)</span></label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                onFocus={() => handleFieldFocus("name")}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                placeholder="John Doe"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                onFocus={() => handleFieldFocus("email")}
                required
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Phone Number</label>
              <input
                type="tel"
                value={form.contactPhone}
                onChange={(e) => update("contactPhone", e.target.value)}
                onFocus={() => handleFieldFocus("phone")}
                required
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                placeholder="+65 9123 4567"
              />
              <p className="text-xs text-stone-400 mt-1">
                We verify your ACRA details to keep the platform secure — we'll be in touch shortly.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                How did you hear about us?
              </label>
              <select
                value={form.heardAboutUs}
                onChange={(e) => update("heardAboutUs", e.target.value)}
                onFocus={() => handleFieldFocus("heard_about_us")}
                required
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              >
                <option value="">Select an option</option>
                <option value="Google search">Google search</option>
                <option value="ChatGPT / AI assistant">ChatGPT / AI assistant</option>
                <option value="Referral from a friend or colleague">Referral from a friend or colleague</option>
                <option value="LinkedIn">LinkedIn</option>
                <option value="Social media">Social media</option>
                <option value="Blog or article">Blog or article</option>
                <option value="GovTech / Singpass partner directory">GovTech / Singpass partner directory</option>
                <option value="Event or conference">Event or conference</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {error && (
              <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors"
            >
              {loading ? "Creating..." : "Create account"}
            </button>

            <p className="text-xs text-stone-400 leading-relaxed text-center mt-3">
              By creating an account, you agree that SingVMS will collect,
              store, and process visitor personal data on your behalf under
              Singapore's PDPA. Visitor data is owned and controlled by SingVMS
              and made available to your organisation for legitimate visitor
              management purposes.
            </p>
          </form>

          <p className="mt-6 text-center text-sm text-stone-500">
            Already have an account?{" "}
            <Link to="/login" className="text-brand-500 hover:underline font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
